import { FC, useEffect, useRef, useState } from 'react';
import { useMethods } from '@lhb/hook';
import { isArray } from '@lhb/func';
import { useSelector } from 'react-redux';
import { ControlType, parseValueCatch, relevancyShow, setComValue,
  getCalculateComs,
  calculateHandle
} from '@/common/components/business/DynamicComponent/config';
import {
  addressChangeCommon,
  handleFootprintTextValue,
} from '@/common/components/business/DynamicComponent/customize';
import styles from './index.module.less';
import V2Form from '@/common/components/Form/V2Form';
import DynamicComponent from '@/common/components/business/DynamicComponent';
import RecoupCostPeriod from '@/common/components/business/ExpandStore/ChanceCreateDrawer/RecoupCostPeriod';
import { AddressChangeParams } from '@/common/components/business/DynamicComponent/components/Address';
import EstimateSalesKWK from '@/common/components/business/ExpandStore/ChanceCreateDrawer/EstimateSalesKWK';
import {
  applyYiMingChanceFieldRules,
  handleYiMingChanceFieldClear,
  YI_MING_EDIT_DEFAULT_IDENTIFICATIONS,
} from '@/common/components/business/DynamicComponent/yiMingChancepoint';

/**
 * @Description 机会点form内容
 */
const Main: FC<{
  formData,
  active,
  // scrollAccordingTab: boolean,
  isLongList: boolean,
  form,
  tiledFormDataRef,
  onValuesChangeRef,
  setFormData,
  setHintStr,
  // mainHeight,
  supportDirectApproval,
  dynamicTabContentRefs,
  /** 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读） */
  isFootprintValue,
  isAreaProtect,
  /** 禁用开店区域点击查看详情 */
  disabledArea,
  /** 当前区域保护特殊申请状态；未配置区域保护时保持 undefined。 */
  antiCollisionApprovalStatus?: number,
  /** 区域保护检测结果变更后，更新详情顶部操作 */
  onAreaProtectionCollisionChange?: (hasCollisionWithin200m: boolean | undefined) => void,
}> = ({
  formData,
  active,
  // scrollAccordingTab = false,
  isLongList = false,
  form,
  tiledFormDataRef,
  onValuesChangeRef,
  setFormData,
  setHintStr,
  // mainHeight,
  supportDirectApproval,
  dynamicTabContentRefs,
  isFootprintValue, // 在编辑状态下，踩点组件是否已有数据（已有数据则设置表单不可读）
  isAreaProtect,
  disabledArea, // 禁用开店区域点击查看详情
  antiCollisionApprovalStatus,
  onAreaProtectionCollisionChange,
}) => {
  const isYiMing = !!useSelector((state: any) => state.common.tenantCheck?.isYiMing);
  const selfRef: any = useRef();
  const returnPeriodRef: any = useRef();
  const [businessPlanValue, setBusinessPlanValue] = useState({});
  const [businessInfoValue, setBusinessInfoValue] = useState({});
  // 卡旺卡预估销售额
  const [addressVal, setAddressVal] = useState<any>(); // 地址组件的值
  const [salesFactorOfKWK, setSalesFactorOfKWK] = useState<any>({}); // 卡旺卡预计销售额影响因子，这里默
  const [isEditedBasicChancePointName, setEditedBasicChancePointName] = useState(false); // 定制化字段，机会点名称，用于判断是否编辑过
  const [customGatheringCategoryForYHT, setCustomGatheringCategoryForYHT] = useState<any>(null);

  useEffect(() => {
    // 长列表时定位到具体group模块，不执行这块处理
    if (isLongList) return;

    if (!active) return;
    const targetDom = selfRef.current;
    if (!targetDom) return;
    targetDom.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  }, [active]);

  const {
    onValuesChange,
    addressChange,
    updateRelateShow,
    updateCompValue,
    computationalLogic,
    getTabContentRefs,
    showHintStr,
  } = useMethods({
    showHintStr() {
      const { hint } = formData;
      hint && setHintStr && setHintStr(hint);
    },
    /**
     * 字段值更新时触发回调事件
     * @param changedFields 当前更新的字段值，格式为{ name: value } // name为表单项的name值，value是当前的值
     */
    onValuesChange(changedFields: any) {
      const curChangedFields = Object.entries(changedFields);
      if (!(isArray(curChangedFields) && curChangedFields.length)) return;
      const targetIdentification = curChangedFields[0][0];
      const val = curChangedFields[0][1];

      handleFootprintTextValue(tiledFormDataRef, targetIdentification, val);

      // 不同二级菜单下可添加相同字段之后，需要把find改成filter
      // const targetCom = tiledFormDataRef.current.find((item: any) => item.identification === targetIdentification);
      // setComValue(targetCom, val);
      // updateRelateShow(targetCom);
      const targetComs = tiledFormDataRef.current.filter((item: any) => item.identification === targetIdentification);
      if (!(isArray(targetComs) && targetComs.length)) return;
      let isNumberInput = false; // 是否是数字输入框组件
      targetComs.forEach((targetCom: any, index: number) => {
        // 竞品组件、品牌组件通过组件回调事件更新
        if ([
          ControlType.CONTEND_INFO.value,
          ControlType.COMPETITIVE_CONDITIONS.value,
          ControlType.BENCH_MARKING_BRANDS.value,
          // 商务条件：内部字段经组件回调整体更新，不走 onValuesChange 部分覆盖
          ControlType.BUSINESS_CONDITION.value,
          // 项目点位选择以完整对象更新，不能用子字段覆盖。
          ControlType.PROJECT_SPOT_SELECT.value,
          ControlType.PROFIT_CAlCULATION_JNP.value].includes(targetCom.controlType)) {
          return;
        }
        setComValue(targetCom, val);
        const { controlType } = targetCom;
        // 相同的字段，其组件类型一定是相同的
        isNumberInput = controlType === ControlType.INPUT_NUMBER.value;
        if (index > 0) return;
        updateRelateShow(targetCom); // 只执行一次
      });

      // 只有数字输入框组件或加盟费-益禾堂（下拉框）才能配置计算公式
      const shouldRunCommonCalculation = isNumberInput || targetIdentification === 'franchiseFeeYHT';
      if (shouldRunCommonCalculation) computationalLogic();
      if (isYiMing) applyYiMingChanceFieldRules({ form, tiledFormData: tiledFormDataRef.current });
      if (!shouldRunCommonCalculation) return;
      // 回本周期定制化逻辑
      returnPeriodRef.current.valuesChange(targetIdentification, val);
    },
    // 计算公式的逻辑
    computationalLogic () {
      // 查找配置了计算公式的组件进行计算
      const calculateComs = getCalculateComs(tiledFormDataRef.current);
      // 所有的数字输入框组件
      const numInputComs = tiledFormDataRef.current.filter((item: any) => item.controlType === ControlType.INPUT_NUMBER.value);
      // 特殊控件字段：加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const specialCOms = tiledFormDataRef.current.filter((item: any) => item.identification === 'franchiseFeeYHT');
      // 计算对象：所有的数字输入框组件+加盟费-益禾堂"franchiseFeeYHT"(下拉框)
      const objCalculateComs = [...numInputComs, ...specialCOms];
      calculateHandle(objCalculateComs, calculateComs);
      // 计算的时候只设置了对应的textValue，还需要赋值到Form上
      numInputComs.forEach((item: any) => {
        const { identification, textValue } = item;
        const value = textValue ? JSON.parse(textValue)?.value : ''; // 当前组件的值
        form.setFieldValue(identification, value);
      });
    },
    /**
     * @description 字段值变动时，刷新关联组件展示
     * @param targetCom 发生字段值变动的属性
     */
    updateRelateShow(targetCom) {
      if (!targetCom) return;
      if (isYiMing) handleYiMingChanceFieldClear({ form, tiledFormData: tiledFormDataRef.current, identification: targetCom.identification });
      // 配置了关联显示的字段时，如果重新渲染
      // const { templateRestriction } = targetCom;
      const targetConfig = parseValueCatch(targetCom, 'templateRestriction');
      if (!targetConfig) return;
      const { activeLinkageRelations } = targetConfig;
      if (isArray(activeLinkageRelations) && activeLinkageRelations.length) {
        if (isYiMing) {
          relevancyShow(tiledFormDataRef.current, true, YI_MING_EDIT_DEFAULT_IDENTIFICATIONS);
        } else {
          relevancyShow(tiledFormDataRef.current);
        }
        // 关联显隐完成后再重算，避免隐藏字段的旧值继续参与计算公式。
        computationalLogic();
        setFormData((state) => ({ ...state, ...formData }));
      }
    },
    /**
     * 详细地址组件的特殊逻辑
     * 通过高德的逆地址解析拿到高德的code，通过接口查找对应的省市区id，然后赋值到省市区组件上
     */
    async addressChange(params: AddressChangeParams) {
      setAddressVal(params);
      addressChangeCommon(params, tiledFormDataRef, form, (compValue) => {
        // 将商圈规划数据传入组件
        setBusinessPlanValue(compValue);
      }, (compValue) => {
        // 将商圈信息数据传入组件
        setBusinessInfoValue(compValue);
      });

      // 目前只在手动变更地址时触发计算公式、编辑回显获取地址时不执行
      if (!params.isInitChange) {
        // 日均销售额预测参与了写死的计算公式[回本周期]的计算，动态赋值之后需要再触发一次计算公式计算
        computationalLogic();
        // 查找一次试算单店月均盈余值
        returnPeriodRef.current.valuesChange('', '');
      }
    },

    /**
     * @description 自定义组件（周边查询、商圈规划、周边竞品等）变动时，更新componentValue
     * @param params 通常为对象
     */
    updateCompValue(identification, params: any) {
      const targetCom = tiledFormDataRef.current.find((item: any) => item.identification === identification);
      setComValue(targetCom, params);
    },
    getTabContentRefs: (el: any, index: number, key: string) => {
      dynamicTabContentRefs.current[index] = {
        el,
        key
      };
    }
  });

  onValuesChangeRef.current = onValuesChange;

  return (
    <>
      <div
        ref={selfRef}
        className={styles.mainCon}
        style={{
          // height: `${mainHeight}px` || 'auto'
        }}>
        <V2Form
          form={form}
          onValuesChange={onValuesChange}>
          {
            formData?.propertyGroupVOList ? <>
              {
                // 遍历一级菜单
                formData?.propertyGroupVOList.map((groupData: any, index) => (<div
                  key={index}
                  ref={(el) => getTabContentRefs(el, index, `${groupData.id}`)}
                >
                  {
                    (isLongList || groupData.id === +active) ? (<>
                      {/* 渲染动态表单用的组件 */}
                      <DynamicComponent
                        form={form}
                        wholeFormData={formData}
                        formData={groupData}
                        showAssociatedRecords
                        // active={active}
                        // scrollAccordingTab={scrollAccordingTab}
                        // anchorCustomStyle={{
                        //   top: '220px',
                        // }}
                        isTraditionEvaluation={false}
                        // needCheck={true}
                        isLongList={isLongList}
                        supportDirectApproval={supportDirectApproval}
                        addressChange={addressChange}
                        updateRelateShow={updateRelateShow}
                        showHintStr={showHintStr}
                        isFootprintValue={isFootprintValue}
                        businessPlanValue={businessPlanValue}
                        businessInfoValue={businessInfoValue}
                        updateCompValue={updateCompValue}
                        isAreaProtect={isAreaProtect}
                        addressVal={addressVal}
                        antiCollisionApprovalStatus={antiCollisionApprovalStatus}
                        salesFactorOfKWK={salesFactorOfKWK}
                        setSalesFactorOfKWK={setSalesFactorOfKWK}
                        isEditedBasicChancePointName={isEditedBasicChancePointName}
                        setEditedBasicChancePointName={setEditedBasicChancePointName}
                        tiledFormDataRef={tiledFormDataRef}
                        customGatheringCategoryForYHT={customGatheringCategoryForYHT}
                        setCustomGatheringCategoryForYHT={setCustomGatheringCategoryForYHT}
                        disabledArea={disabledArea}
                        isEdit
                        onAreaProtectionCollisionChange={onAreaProtectionCollisionChange}
                      />
                    </>) : null
                  }
                </div>))
              }
            </>
              : null
          }
        </V2Form>

        {/* 回本周期 */}
        <RecoupCostPeriod
          ref={returnPeriodRef}
          form={form}
          tiledFormDataRef={tiledFormDataRef}
          updateCompValue={updateCompValue}/>
        {/* 卡旺卡预计门店销售额 */}
        <EstimateSalesKWK
          form={form}
          addressVal={addressVal}
          businessInfoValue={businessInfoValue}
          salesFactorOfKWK={salesFactorOfKWK}
          tiledFormDataRef={tiledFormDataRef}
        />
      </div>
    </>
  );
};

export default Main;
